// JobsTable.js
import React, { useState, useEffect, useContext } from 'react';
import StatusUpdateDialog from './StatusUpdateDialog';
import GoaRequestDialog from './JobActionDialogs/GoaRequestDialog'; // Import GOA Dialog
import UnsuccessfulRequestDialog from './JobActionDialogs/UnsuccessfulRequestDialog'; // Import Unsuccessful Dialog
import {
  Box,
  Typography,
  Table,
  TableContainer, // Import TableContainer
  TableHead,
  TableRow,
  TableCell,
  useTheme, // Import useTheme for breakpoints
  useMediaQuery, // Import useMediaQuery for breakpoints
  TableBody,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Menu,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper, // Import Paper for TableContainer
  Skeleton,
  ListItemIcon, // Import for icons in menu
  ListItemText, // Import for text in menu
  Switch, // Import Switch component
  Tooltip, // Import Tooltip for hover text
  Chip, // Import Chip for status display
  Card, // Import Card for mobile view
  CardContent, // Import CardContent for mobile view
  CardActions, // Import CardActions for mobile view
  Grid, // Import Grid for layout within Card
  Stack // Import Stack for mobile card layout
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon, // Icon for Cancel Job
  SkipNext as SkipNextIcon, // Icon for Cancel SP & Find Next
  FindReplace as FindReplaceIcon, // Icon for Find Another Driver
  InfoOutlined as InfoOutlinedIcon, // Icon for Empty State
  CheckCircleOutline as CheckCircleOutlineIcon, // Status: Completed
  HourglassEmpty as HourglassEmptyIcon, // Status: Pending, Pending Acceptance
  Search as SearchIcon, // Status: Searching SP
  Schedule as ScheduleIcon, // Status: Scheduled
  LocalShipping as LocalShippingIcon, // Status: Dispatched, En Route
  LocationOn as LocationOnIcon, // Status: On Site
  ReportProblem as ReportProblemIcon, // Status: Unsuccessful, GOA Denied, Rejected
  ThumbUp as ThumbUpIcon, // Status: GOA
  HelpOutline as HelpOutlineIcon, // Status: Awaiting Approval, Pending ETA Approval
  ErrorOutline as ErrorOutlineIcon, // Status: Canceled
  PersonRemove as PersonRemoveIcon, // Icon for Unassign from Service Provider
  Campaign as CampaignIcon, // Blast dispatch (broadcast to all providers in radius)
  CreditCard as CreditCardIcon, // Card payment for completed credit-card jobs
} from '@mui/icons-material';
import CountdownTimer from './CountdownTimer';
import JobDetails from './JobDetails';
// import { ALL_JOB_STATUSES } from '../constants/jobConstants'; // Unused import
import {
  calculateTargetTime,
  calculateAutoRejectTime,
  isJobProvider,
  isAssignedDriver,
  canApproveDenyETA,
  canApproveOrg1DriverETA
 } from '../utils/jobUtils';
 import { useUser } from '../context/UserContext'; // Import useUser hook
 import { useLoading } from '../context/LoadingContext'; // Import useLoading hook
 import {
   canDispatchJobs, // Import canDispatchJobs
   canManageGoaRequests,
   canManageUnsuccessfulRequests,
   getAllowedStatusTransitions,
   isDriverOnly, // Renamed from isDriverUser
   hasPermission,
   getUserOrganizationLevel // Import getUserOrganizationLevel
 } from '../utils/permissionStore'; // Updated import source
 import {
   handleToggleAutomatedDispatch,
   handleApproveOrg1ETA, // Import new handler
   handleDenyOrg1ETA // Import new handler
 } from '../utils/jobActionHandlers'; // Import the handler
 import axios from 'axios'; // Import axios for API calls
 import { ThemeContext } from '../context/ThemeContext';
 import { useOrg } from '../context/OrgContext';

 // Location display: legacy flat string when present, else built from the
 // structured serviceLocation (jobs created outside the portal form lack the
 // legacy string).
 const jobLocationText = (job) => {
   if (job.location) return job.location;
   const loc = job.serviceLocation;
   if (loc && typeof loc === 'object') {
     const parts = [loc.street, loc.city, loc.state && loc.zip ? `${loc.state} ${loc.zip}` : loc.state || loc.zip].filter(Boolean);
     if (parts.length > 0) return parts.join(', ');
   }
   return '';
 };

 const JobsTable = ({
  jobs,
  // currentUser, // Removed currentUser prop
  jobCategory,
  loading,
  onExpandClick,
  onEditJob,
  onAcceptJob,
  onRejectJob,
  onDispatchJob, // Prop to open dispatch dialog
  onBlastJob, // Prop to open blast dispatch dialog (radius broadcast)
  onCardPayment, // Prop to open card payment dialog for completed credit-card jobs
  onJobStatusChange,
  onMenuAction,
  onDeleteJob,
  onCancelSPAssignment, // Add new prop for cancelling SP assignment
  onFindNextOrg1Driver, // Add new prop for finding next Org 1 driver
  setJobs, // Add setJobs prop for updating local state after toggle
  refreshJobs, // Add refreshJobs prop
  setNotification // Add setNotification prop
  // Removed setJobForOrg1DriverEtaDecision and setOrg1DriverEtaDecisionDialogOpen props
}) => {
  const { name: orgName } = useOrg();
  // General Action Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedJobForMenu, setSelectedJobForMenu] = useState(null);

  // SP Assignment Cancellation Menu state
  const [spMenuAnchorEl, setSPMenuAnchorEl] = useState(null);
  const [selectedJobForSPMenu, setSelectedJobForSPMenu] = useState(null);

  // Org 1 Driver Assignment Menu state
  const [driverMenuAnchorEl, setDriverMenuAnchorEl] = useState(null);
  const [selectedJobForDriverMenu, setSelectedJobForDriverMenu] = useState(null);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  
  // Status update dialog for drivers
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [jobForStatusUpdate, setJobForStatusUpdate] = useState(null);
  
  // GOA approval/denial confirmation dialog states
  const [approveGOADialogOpen, setApproveGOADialogOpen] = useState(false);
  const [denyGOADialogOpen, setDenyGOADialogOpen] = useState(false);
  const [jobForGOAAction, setJobForGOAAction] = useState(null);
  // Unsuccessful approval/denial confirmation dialog states
  const [approveUnsuccessfulDialogOpen, setApproveUnsuccessfulDialogOpen] = useState(false);
  const [denyUnsuccessfulDialogOpen, setDenyUnsuccessfulDialogOpen] = useState(false);
  const [jobForUnsuccessfulAction, setJobForUnsuccessfulAction] = useState(null);
  
  // State for GOA/Unsuccessful Request Dialogs
  const [goaRequestDialogOpen, setGoaRequestDialogOpen] = useState(false);
  const [unsuccessfulRequestDialogOpen, setUnsuccessfulRequestDialogOpen] = useState(false);
  const [jobForRequestDialog, setJobForRequestDialog] = useState(null);
  
  // Get user from context
  const { currentUser, isLoading: userLoading, error: userError } = useUser();
  const { startLoading } = useLoading(); // Get startLoading from context
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // Check if screen is extra-small or smaller
  const { mode } = useContext(ThemeContext);

  // Use state to track delete permissions
  const [canDeleteJobs, setCanDeleteJobs] = useState(false);
  
  // Force render flag - we'll use this to force re-renders when needed
  const [forceRender, setForceRender] = useState(0);

  // Define styles (color and icon) for each status - INSIDE component scope
  const [statusStyles, setStatusStyles] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/dashboard-settings', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { jobStatusColors } = res.data;
        const newStatusStyles = {};
        for (const [status, color] of Object.entries(jobStatusColors)) {
          newStatusStyles[status] = { color, icon: getStatusIcon(status) };
        }
        setStatusStyles(newStatusStyles);
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };

    fetchSettings();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <CheckCircleOutlineIcon fontSize="small" />;
      case 'Pending':
      case 'Pending Acceptance':
      case 'Pending Driver Acceptance':
        return <HourglassEmptyIcon fontSize="small" />;
      case 'Searching SP':
        return <SearchIcon fontSize="small" />;
      case 'Blast Dispatch':
        return <CampaignIcon fontSize="small" />;
      case 'Scheduled':
        return <ScheduleIcon fontSize="small" />;
      case 'Dispatched':
      case 'En Route':
        return <LocalShippingIcon fontSize="small" />;
      case 'On Site':
        return <LocationOnIcon fontSize="small" />;
      case 'Canceled':
        return <ErrorOutlineIcon fontSize="small" />;
      case 'Rejected':
      case 'GOA Denied':
      case 'Unsuccessful':
        return <ReportProblemIcon fontSize="small" />;
      case 'Awaiting Approval':
      case 'Pending ETA Approval':
        return <HelpOutlineIcon fontSize="small" />;
      case 'GOA':
        return <ThumbUpIcon fontSize="small" />;
      default:
        return <HelpOutlineIcon fontSize="small" />;
    }
  };

  // Helper function to get style object for a status - INSIDE component scope
  const getStatusStyle = (status) => {
    return statusStyles[status] || { color: 'default', icon: <HelpOutlineIcon fontSize="small" /> };
  };
  
  // When any job's expanded state changes, trigger a re-render of the entire component
  useEffect(() => {
    // Force re-render when jobs change - especially expanded state
    setForceRender(prev => prev + 1);
  }, [jobs]); // Depend directly on jobs instead of a derived value
  
  // Special handling for canceled jobs tab to ensure delete icons appear
  useEffect(() => {
    if (jobCategory === 'canceled') {
      // console.log('In canceled jobs tab, triggering permission update');
      setForceRender(prev => prev + 1);
    }
  }, [jobCategory]);
  
  // Update permissions when anything relevant changes
  useEffect(() => {
    const hasDeletePermission = hasPermission('deleteJobs');
    setCanDeleteJobs(hasDeletePermission);
    
    // Debug logging for delete button visibility
    // console.log('JobsTable permission update:', { 
    //   jobCategory, 
    //   hasDeletePermission,
    //   shouldShowDeleteButton: jobCategory === 'canceled' && hasDeletePermission,
    //   jobCount: jobs.length,
    //   forceRender
    // });
    
    // If in canceled category, do an additional permission setting after a short delay
    // This helps ensure the permissions are updated after the component has fully rendered
    if (jobCategory === 'canceled') {
      setTimeout(() => {
        setCanDeleteJobs(prevState => {
          // console.log('Delayed permission update for canceled jobs tab:', hasDeletePermission);
          return hasDeletePermission;
        });
      }, 500);
    }
  }, [currentUser, jobCategory, jobs.length, forceRender]);
  
  // Handle delete button click
  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (jobToDelete && onDeleteJob) {
      onDeleteJob(jobToDelete.id);
    }
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };
  
  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };

  // Handle menu click
  const handleMenuClick = (event, job) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedJobForMenu(job);
  };

  // Handle general menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedJobForMenu(null);
  };

  // Handle SP menu click
  const handleSPMenuClick = (event, job) => {
    setSPMenuAnchorEl(event.currentTarget);
    setSelectedJobForSPMenu(job);
  };

  // Handle SP menu close
  const handleSPMenuClose = () => {
    setSPMenuAnchorEl(null);
    setSelectedJobForSPMenu(null);
  };

  // Handle Driver menu click
  const handleDriverMenuClick = (event, job) => {
    setDriverMenuAnchorEl(event.currentTarget);
    setSelectedJobForDriverMenu(job);
  };

  // Handle Driver menu close
  const handleDriverMenuClose = () => {
    setDriverMenuAnchorEl(null);
    setSelectedJobForDriverMenu(null);
  };

  // Handle general menu action selection
  const handleMenuActionSelect = (action) => {
    if (!selectedJobForMenu || !currentUser) {
      handleMenuClose();
      return;
    }
 
    // Handle GOA/Unsuccessful requests by opening dialogs
    if (action === 'markGOA') {
      setJobForRequestDialog(selectedJobForMenu);
      setGoaRequestDialogOpen(true);
    } else if (action === 'markUnsuccessful') {
      setJobForRequestDialog(selectedJobForMenu);
      setUnsuccessfulRequestDialogOpen(true);
    } else {
      // Handle other actions directly
      onMenuAction(action, selectedJobForMenu, currentUser);
    }
 
    handleMenuClose(); // Close the menu regardless
  };

  // Render the job driver cell content based on status
  const renderDriverCell = (job) => {
    if (!currentUser) return <Skeleton variant="text" width={80} />; // Show skeleton if user not loaded

    // --- Handle "Pending ETA Approval" Status ---
    if (job.status === 'Pending ETA Approval') {
      // Determine if this is an Org 1 driver ETA or an SP ETA
      const isOrg1DriverETA = job.pendingOrg1DriverETA !== undefined && job.submittedOrg1DriverETABy;
      const isSPETA = job.pendingSP_ETA !== undefined && job.submittedSP_ETA_By;
      
      console.log(`[JobsTable] Pending ETA Approval for job ${job.id}: isOrg1DriverETA=${isOrg1DriverETA}, isSPETA=${isSPETA}`);
      
      // SP User viewing job assigned to their org - ONLY show "Awaiting Approval"
      const userVendorIdentifier = currentUser.vendorNumber || currentUser.vendorId;
      const isAssignedToCurrentUserOrg = job.assignedVendorNumber === userVendorIdentifier;
      const canActOnSPJob = currentUser.primaryRole === 'SP' ||
                           currentUser.secondaryRoles?.admin === true ||
                           currentUser.secondaryRoles?.dispatcher === true ||
                           currentUser.secondaryRoles?.answeringService === true;

      if (isSPETA && isAssignedToCurrentUserOrg && canActOnSPJob) {
        return (
          <Box>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Awaiting Approval
            </Typography>
          </Box>
        );
      }
      // Handle Org 1 driver ETA approval
      else if (isOrg1DriverETA && canApproveOrg1DriverETA(currentUser, job)) {
        // Get the driver name who submitted the ETA
        const driverName = job.driver || 'Org 1 Driver';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {driverName}
              <Typography variant="caption" display="block" color="text.secondary">
                (ETA: {job.pendingOrg1DriverETA} minutes)
              </Typography>
            </Typography>
            {/* Render Approve/Reject buttons directly */}
            <Box sx={{ ml: 2 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                sx={{ mr: 1, mb: 1 }}
                onClick={() => handleApproveOrg1ETA(job, startLoading, refreshJobs, setNotification)}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleDenyOrg1ETA(job, startLoading, refreshJobs, setNotification)}
              >
                Reject
              </Button>
            </Box>
          </Box>
        );
      }
      // Handle SP ETA approval
      else if (isSPETA && canApproveDenyETA(currentUser, job)) {
        // Org 1 user with appropriate roles viewing job that needs SP ETA approval
        const companyName = job.assignedSPCompanyName || job.assignedVendorNumber || 'Assigned SP';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {companyName}
              <Typography variant="caption" display="block" color="text.secondary">
                (ETA: {job.pendingSP_ETA} minutes)
              </Typography>
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                sx={{ mr: 1, mb: 1 }}
                onClick={() => {
                  console.log(`[JobsTable] Approve SP ETA button clicked for job: ${job.id}`);
                  onMenuAction('approveETA', job, currentUser);
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => {
                  console.log(`[JobsTable] Deny SP ETA button clicked for job: ${job.id}`);
                  onMenuAction('denyETA', job, currentUser);
                }}
              >
                Deny
              </Button>
            </Box>
          </Box>
        );
      } else {
        // Other users just see the status and entity pending approval
        if (isOrg1DriverETA) {
          return (
            <Typography>
              {job.driver || 'Org 1 Driver'} (Pending ETA Approval)
            </Typography>
          );
        } else {
          return (
            <Typography>
              {job.assignedVendorNumber || 'SP'} (Pending Approval)
            </Typography>
          );
        }
      }
    }
    // --- END: Handle "Pending ETA Approval" Status ---
    
    // --- Handle "Blast Dispatch" Status (offer broadcast to all providers in radius) ---
    else if (job.status === 'Blast Dispatch') {
      const bd = job.blastDispatch || {};
      const blastVendorId = currentUser.vendorNumber || currentUser.vendorId;
      // Support both array-form and object-form secondaryRoles
      const rolesRaw = currentUser.secondaryRoles || {};
      const rolesArr = Array.isArray(rolesRaw)
        ? rolesRaw
        : Object.entries(rolesRaw).filter(([, v]) => v === true).map(([k]) => k);
      const canActForSP = currentUser.primaryRole === 'SP' || rolesArr.includes('admin') || rolesArr.includes('dispatcher') || rolesArr.includes('answeringService');
      const isSPBlastRecipient = currentUser.organizationLevel === 2 && blastVendorId &&
        (bd.vendorNumbers || []).includes(blastVendorId) && canActForSP;
      const isDriverBlastRecipient = (bd.driverIds || []).some(
        id => (id?._id || id)?.toString() === currentUser._id?.toString()
      );

      if (bd.active && (isSPBlastRecipient || isDriverBlastRecipient)) {
        // Recipient view: looks like a normal job offer (Accept/Reject + timer).
        // If someone else accepts first, the backend responds with a clear
        // "another provider has already accepted" message on accept.
        return (
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ mr: 1, mb: 1 }}
              onClick={() => onAcceptJob(job)}
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onRejectJob(job.id)}
            >
              Reject
            </Button>
            {job.autoRejectAt && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="error">
                  Auto-reject in:
                </Typography>
                <CountdownTimer targetTime={calculateAutoRejectTime(job)} tooltipPrefix="Auto-reject in:" />
              </Box>
            )}
          </Box>
        );
      } else if (currentUser.organizationLevel === 1) {
        // Org 1 dispatcher/owner view: blast progress summary
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CampaignIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">
              Blasting {(bd.vendorNumbers || []).length} SP{(bd.vendorNumbers || []).length === 1 ? '' : 's'} + {(bd.driverIds || []).length} driver{(bd.driverIds || []).length === 1 ? '' : 's'}
              <Typography variant="caption" display="block" color="text.secondary">
                Round {bd.round || 1} of {bd.maxRounds || 2} — within {bd.radiusMiles || '?'} mi
              </Typography>
              {job.autoRejectAt && (
                <Box sx={{ display: 'block' }}>
                  <Typography variant="caption" color="warning.main" component="span">
                    (Round ends in:
                  </Typography>
                  <CountdownTimer targetTime={calculateAutoRejectTime(job)} wrapper="span" />
                  <Typography variant="caption" color="warning.main" component="span">
                    )
                  </Typography>
                </Box>
              )}
            </Typography>
          </Box>
        );
      } else {
        return (
          <Typography variant="body2" color="text.secondary">
            Blast dispatch in progress...
          </Typography>
        );
      }
    }
    // --- END: Handle "Blast Dispatch" Status ---

    // --- Failed blast awaiting Org 1 response ---
    else if (job.status === 'Waiting' && job.blastDispatch?.failed) {
      const bd = job.blastDispatch || {};
      return (
        <Box>
          <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
            Blast failed — no acceptance within {bd.radiusMiles || '?'} mi
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {bd.round || 1} round{(bd.round || 1) === 1 ? '' : 's'} completed (started at {bd.initialRadiusMiles || '?'} mi)
          </Typography>
          {job.autoRejectAt && (
            <Box sx={{ display: 'block' }}>
              <Typography variant="caption" color="warning.main" component="span">
                Auto-dispatch resumes in:
              </Typography>
              <CountdownTimer targetTime={calculateAutoRejectTime(job)} wrapper="span" />
            </Box>
          )}
          {currentUser.organizationLevel === 1 && canDispatchJobs() && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => onDispatchJob(job)}
            >
              Dispatch
            </Button>
          )}
        </Box>
      );
    }
    // --- END: Failed blast awaiting response ---

    // --- Handle "Searching SP" Status ---
    else if (job.status === 'Searching SP') {
      const userVendorIdentifier = currentUser.vendorNumber || currentUser.vendorId;
      const isAssignedToCurrentUserOrg = job.assignedVendorNumber === userVendorIdentifier;
      // Correctly check if secondaryRoles is an array and includes the role
      const secondaryRolesArray = Array.isArray(currentUser.secondaryRoles) ? currentUser.secondaryRoles : [];
      const isPrimarySP = currentUser.primaryRole === 'SP';
      const hasAdminRole = secondaryRolesArray.includes('admin');
      const hasDispatcherRole = secondaryRolesArray.includes('dispatcher');
      const hasAnsweringServiceRole = secondaryRolesArray.includes('answeringService');
      const canActOnSPJob = isPrimarySP || hasAdminRole || hasDispatcherRole || hasAnsweringServiceRole;
      
      // Logs removed, logic corrected above

      if (isAssignedToCurrentUserOrg && canActOnSPJob) {
        console.log(`[JobsTable SP View] Job ID: ${job.id}, Status: ${job.status}, AutoDispatch: ${job.automatedDispatchEnabled}, AutoRejectAt: ${job.autoRejectAt}`); // DEBUG LOG
        // console.log(`[renderDriverCell - Searching SP] Job ID: ${job.id} - Rendering Accept/Reject for SP Org User.`);
        // SP User viewing job assigned to their org
        return (
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ mr: 1, mb: 1 }}
              onClick={() => onAcceptJob(job)} // Reuse onAcceptJob for now, handler logic will differentiate
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onRejectJob(job.id)} // Reuse onRejectJob for now
            >
              Reject
            </Button>
            {job.autoRejectAt && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="error">
                  Auto-reject in:
                </Typography>
                <CountdownTimer targetTime={calculateAutoRejectTime(job)} tooltipPrefix="Auto-reject in:" />
              </Box>
            )}
          </Box>
        );
      } else if (job.assignedVendorNumber && isJobProvider(job, currentUser)) { // Reinstated check for assignedVendorNumber
        // console.log(`[renderDriverCell - Searching SP] Job ID: ${job.id} - Rendering Org 1 view (Assigned SP Name + Menu).`);
        // Org 1 user viewing job assigned to an SP - Show dropdown menu
        const companyName = job.assignedSPCompanyName || job.assignedVendorNumber || 'Assigned SP';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {companyName}
              <Typography variant="caption" display="block" color="text.secondary">
                (Pending SP Acceptance)
              </Typography>
              {/* Add Timer for Org 1 view */}
              {/* DEBUG LOG */}
              {/* {console.log(`[JobsTable Render] Job ${job.id} (Searching SP - Org 1 View): autoRejectAt = ${job.autoRejectAt}`)} */}
              {job.autoRejectAt && (
                <Box sx={{ display: 'block' }}>
                  <Typography variant="caption" color="warning.main" component="span">
                    (Auto-reject in:
                  </Typography>
                  <CountdownTimer targetTime={calculateAutoRejectTime(job)} wrapper="span" />
                  <Typography variant="caption" color="warning.main" component="span">
                    )
                  </Typography>
                </Box>
              )}
            </Typography>
            <Tooltip title="Service Provider Actions">
              <IconButton
                aria-label="Service provider actions"
                size="small"
                onClick={(e) => handleSPMenuClick(e, job)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      } else {
         // This block is reached if:
         // - User is NOT the assigned SP org
         // - AND User is NOT the Org 1 provider viewing an assigned SP job
         // - OR job.assignedVendorNumber is null/undefined

         // If it's an Org 1 user viewing an UNASSIGNED 'Searching SP' job, show Dispatch button
         if (isJobProvider(job, currentUser) && !job.assignedVendorNumber) {
             return (
                 <Button
                     variant="contained"
                     color="primary"
                     size="small"
                     onClick={() => onDispatchJob(job)}
                     disabled={!canDispatchJobs()}
                 >
                     Dispatch
                 </Button>
             );
         } else {
             // For other users, or if somehow assignedVendorNumber exists but user isn't provider
             // Just show generic text indicating it's searching
             return (
                 <Typography>
                     Searching SP...
                     {/* Optionally show vendor number if it exists but user isn't provider */}
                     {/* {job.assignedVendorNumber && <Typography variant="caption">({job.assignedVendorNumber})</Typography>} */}
                 </Typography>
             );
         }
      }
    }
    // --- END: Handle "Searching SP" Status ---

    else if (job.status === 'Pending Acceptance' || job.status === 'Pending Driver Acceptance') { // Changed to else if
      // For Pending Acceptance jobs (DRIVER assignment), show different UI based on user role
      // console.log(`[renderDriverCell - Pending Acceptance] Job ${job.id} - User ID: ${currentUser?._id}, Job driverId:`, job.driverId);
      // console.log(`[renderDriverCell - Pending Acceptance] Job ID: ${job.id} - Checking if user ${currentUser?.username} (${currentUser?._id}) is assigned driver`);
      const isAssigned = isAssignedDriver(job, currentUser); // Use currentUser from context
      // console.log(`[renderDriverCell - Pending Acceptance] Job ID: ${job.id} - isAssignedDriver result: ${isAssigned}`);
      if (isAssigned) {
        // If current user is the assigned driver, show Accept/Reject buttons with countdown
        return (
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ mr: 1, mb: 1 }}
              onClick={() => onAcceptJob(job)}
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onRejectJob(job.id)}
            >
              Reject
            </Button>
            {job.autoRejectAt && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="error">
                  Auto-reject in:
                </Typography>
                <CountdownTimer targetTime={calculateAutoRejectTime(job)} tooltipPrefix="Auto-reject in:" />
              </Box>
            )}
          </Box>
        );
      } else if (isJobProvider(job, currentUser)) { // Use currentUser from context
        // If current user is the job provider (dispatcher), show driver name and dropdown menu
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {job.driver}
              {job.autoRejectAt && (
                <Box sx={{ display: 'block' }}>
                  <Typography variant="caption" color="warning.main" component="span">
                    (Auto-reject in:
                  </Typography>
                  <CountdownTimer targetTime={calculateAutoRejectTime(job)} tooltipPrefix="Auto-reject in:" wrapper="span" />
                  <Typography variant="caption" color="warning.main" component="span">
                    )
                  </Typography>
                </Box>
              )}
            </Typography>
            <Tooltip title="Driver Actions">
              <IconButton
                aria-label="Driver actions"
                size="small"
                onClick={(e) => handleDriverMenuClick(e, job)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      } else {
        // For other users, show driver name and timer if applicable
        return (
          <Box>
            <Typography>
              {job.driver} <Typography variant="caption">(Pending)</Typography>
            </Typography>
            {/* Add Timer for other users view */}
            {job.autoRejectAt && (
              <Box sx={{ display: 'block' }}>
                <Typography variant="caption" color="warning.main" component="span">
                  (Auto-reject in:
                </Typography>
                <CountdownTimer targetTime={calculateAutoRejectTime(job)} wrapper="span" />
                <Typography variant="caption" color="warning.main" component="span">
                  )
                </Typography>
              </Box>
            )}
          </Box>
        );
      }
    } else if (job.driver) {
      // If not pending acceptance but has a driver, show the driver name
      return job.driver;
    } else if (job.status === 'Pending Acceptance' && isAssignedDriver(job, currentUser)) { // Use currentUser from context
      // Only show Accept button if job status is Pending Acceptance and current user is the assigned driver
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onAcceptJob(job)}
        >
          Accept
        </Button>
      );
    } else if (job.status === 'Pending') {
      // For Pending jobs, check if it was previously assigned and expired
      const lastRejection = job.rejectedBy && job.rejectedBy.length > 0 ? job.rejectedBy[job.rejectedBy.length - 1] : null;
      if (lastRejection && lastRejection.reason && lastRejection.reason.includes('Auto-expired')) {
         // Show expired message with the previous driver name
        return (
          <Box>
            <Typography variant="body2" color="error.main">
              Acceptance expired
            </Typography>
            <Typography variant="caption" color="text.secondary">
              by: {lastRejection.driverName}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => onDispatchJob(job)}
              disabled={!canDispatchJobs()} // Disable if user cannot dispatch
            >
              Reassign
            </Button>
          </Box>
        );
      } else {
        // Regular Pending job - show Dispatch button
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onDispatchJob(job)}
            disabled={!canDispatchJobs()} // Disable if user cannot dispatch
          >
            Dispatch
          </Button>
        );
      }
    } else if (job.status === 'Scheduled') {
      // Show Dispatch button for scheduled jobs
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onDispatchJob(job)}
          disabled={!canDispatchJobs()} // Disable if user cannot dispatch
        >
          Dispatch
        </Button>
      );
    } else {
      // Fallback for other statuses (like Dispatched, En Route, On Site)
      // Show Reassign button if user can dispatch
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onDispatchJob(job)}
          disabled={!canDispatchJobs()} // Disable if user cannot dispatch
        >
          Reassign
        </Button>
      );
    }
  };

  // Helper function to get allowed status options based on permissions and current job status
  // Mirrors the server rule: driver-work statuses need an assigned driver who has accepted
  const DRIVER_WORK_STATUSES = ['Dispatched', 'En Route', 'On Site', 'Completed'];
  const getAllowedStatusOptions = (job, category) => {
    const options = getPermittedStatusOptions(job, category);
    const driverAccepted = Boolean(job.driverId && job.acceptedAt);
    return driverAccepted
      ? options
      : options.filter(s => s === job.status || !DRIVER_WORK_STATUSES.includes(s));
  };

  const getPermittedStatusOptions = (job, category) => {
    // Default options based on job category
    const defaultOptions = category === 'completed' 
      ? ['Waiting', 'Dispatched', 'En Route', 'On Site', 'Completed', 'GOA', 'Unsuccessful']
      : ['Waiting', 'Dispatched', 'En Route', 'On Site', 'Completed'];
    
    // If no currentUser (still loading?), return empty array or default? Let's return empty for safety.
    if (!currentUser) return [];
    
    // Use the permission system to get allowed transitions
    const allowedTransitions = getAllowedStatusTransitions(job.status); // Permission store uses its own user data source
    
    // If we have allowed transitions from the permission system, use them
    if (allowedTransitions && allowedTransitions.length > 0) {
      // console.log(`Permission-based status options for job ${job.id} (${job.status}):`, allowedTransitions);
      return allowedTransitions;
    }
    
    // Special handling for completed/canceled jobs
    if (category === 'completed' || job.status === 'Completed' || job.status === 'GOA' || job.status === 'Unsuccessful') {
      // Check if user has permission to update jobs in completed tabs
      const canUpdateCompletedJobs = hasPermission('updateJobsInCompletedTabs');
      if (!canUpdateCompletedJobs) {
        // console.log(`User cannot change status of ${job.status} job in completed tab`);
        return [];
      }
      // Use default options for status changes in completed tabs
      return defaultOptions;
    }
    
    // Special handling for canceled jobs
    if (category === 'canceled' || job.status === 'Canceled') {
      // Check if user has permission to update jobs in canceled tabs
      const canUpdateCanceledJobs = hasPermission('updateJobsInCanceledTabs');
      if (!canUpdateCanceledJobs) {
        // console.log(`User cannot change status of ${job.status} job in canceled tab`);
        return [];
      }
      // Use default options for status changes in canceled tabs
      return defaultOptions;
    }
    
    // For all other cases, return the default options
    return defaultOptions;
  };

  // Render the job status cell content
  const renderStatusCell = (job) => {
    if (!currentUser) return <Skeleton variant="text" width={100} />; // Show skeleton if user not loaded

    // Get the allowed status options for this job based on user role
    const allowedStatusOptions = getAllowedStatusOptions(job, jobCategory); // Removed user argument
    
    // Log the allowed options for debugging
    // console.log(`Job ${job.id} allowedStatusOptions:`, allowedStatusOptions);
    
    // Check for special cases where we don't want to show the dropdown
    
    // Check if job has a pending unsuccessful request
    if (job.approvalStatusUnsuccessful === 'pending' && job.unsuccessfulReason) {
      // Check if current user can manage unsuccessful requests
      const canManage = canManageUnsuccessfulRequests(currentUser); // Use currentUser
      
      return (
        <Box>
          <Typography variant="body2" color="error.main">
            Unsuccessful Requested
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Reason: {job.unsuccessfulReason}
          </Typography>
          
          {/* Show Approve/Deny buttons only for users who can manage unsuccessful requests */}
          {canManage && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
              onClick={() => onMenuAction('approveUnsuccessful', job, currentUser)} // Pass currentUser
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => onMenuAction('denyUnsuccessful', job, currentUser)} // Pass currentUser
              >
                Deny
              </Button>
            </Box>
          )}
        </Box>
      );
    } else if (job.status === 'Awaiting Approval') {
      const isGoaRequest = !!job.goaReason;
      const isUnsuccessfulRequest = !!job.unsuccessfulReason;
      // A provider only decides while their driver's GOA request is still theirs; after that it is White Knight's call
      const providerStageOver = currentUser?.organizationLevel === 2 && job.assignedVendorNumber && job.goaSpApproval?.status !== 'pending';
      const canManage = isGoaRequest
        ? canManageGoaRequests(currentUser) && !providerStageOver
        : canManageUnsuccessfulRequests(currentUser); // Check correct permission
      const requestType = isGoaRequest ? 'GOA' : 'Unsuccessful';
      const reason = isGoaRequest ? job.goaReason : job.unsuccessfulReason;
      const approvalStatus = isGoaRequest ? job.approvalStatus : job.approvalStatusUnsuccessful;
      const approveAction = isGoaRequest ? 'approveGOA' : 'approveUnsuccessful';
      const denyAction = isGoaRequest ? 'denyGOA' : 'denyUnsuccessful';
 
      // Check if the request was already denied
      if (approvalStatus === 'rejected') {
        // Define canReconsider based on user permissions
        const canReconsider = hasPermission('reconsiderDeniedRequests');
        return (
          <Box>
            <Typography variant="body2" color="error.main">
              {requestType} Request Denied
            </Typography>
            {/* Show Reconsider button for Org 1 */}
            {canReconsider && (
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => onMenuAction('reconsiderDenial', job, currentUser)} // Need to handle this action
              >
                Reconsider
              </Button>
            )}
             {/* Org 2 users see nothing actionable here, maybe just the reason? */}
             {!canReconsider && reason && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Reason: {reason}
                </Typography>
             )}
          </Box>
        );
      }
 
      // Display pending status and reason
      return (
        <Box>
          <Typography variant="body2" color="warning.main">
            {job.status}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {requestType} request pending
            {isGoaRequest && job.assignedVendorNumber && job.goaSpApproval?.status === 'pending' ? ' — with the provider' : ''}
            {isGoaRequest && job.assignedVendorNumber && job.goaSpApproval?.status === 'approved' ? ` — provider approved, ${orgName} to decide` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Reason: {reason}
          </Typography>

          {/* Show Approve/Deny buttons only for users who can manage */}
          {canManage && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => {
                  if (isGoaRequest) {
                    setJobForGOAAction(job);
                    setApproveGOADialogOpen(true);
                  } else {
                    setJobForUnsuccessfulAction(job);
                    setApproveUnsuccessfulDialogOpen(true);
                  }
                }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => {
                  if (isGoaRequest) {
                    setJobForGOAAction(job);
                    setDenyGOADialogOpen(true);
                  } else {
                    setJobForUnsuccessfulAction(job);
                    setDenyUnsuccessfulDialogOpen(true);
                  }
                }}
              >
                Deny
              </Button>
            </Box>
          )}
        </Box>
      );
    } else if (job.status === 'Pending Acceptance' && isAssignedDriver(job, currentUser)) { // Use currentUser
      // If current user is the assigned driver and job is pending acceptance,
      // don't show dropdown (Accept/Reject buttons are shown in the driver cell)
      // Display static status using styled Chip
      const style = getStatusStyle(job.status);
      return <Chip icon={style.icon} label={job.status} color={style.color} size="small" />;
    } else if (job.status === 'Pending' && job.rejectionReason) {
      // Show "Rejected" if status is Pending and there's a rejection reason
      return (
        <Box>
          <Typography variant="body2" color="error.main">
            Rejected
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Reason: {job.rejectionReason}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => onDispatchJob(job)}
            disabled={!canDispatchJobs()} // Disable if user cannot dispatch
          >
            Reassign
          </Button>
        </Box>
      );
      // --- End Driver View ---
    } else if (isDriverOnly(currentUser?.primaryRole, currentUser?.secondaryRoles) || checkIfDriverFromLocalStorage()) { // Use currentUser
      // --- Driver View ---
      // For "driver only" users - ALWAYS use button interface
      // First check if there are any allowed options 
      if (allowedStatusOptions.length === 0 || jobCategory === 'completed' || jobCategory === 'canceled') {
        // For jobs in completed or canceled tabs, or if no options available, show status as Chip
        // Display static status using styled Chip
        const style = getStatusStyle(job.status);
        return <Chip icon={style.icon} label={job.status} color={style.color} size="small" />;
      }
      
      // For active jobs with allowed options, show current status with an "Update Status" button
      // NEVER use a dropdown for drivers, use a button that opens a dialog instead
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {/* Display current status using styled Chip */}
          <Chip
            icon={getStatusStyle(job.status).icon}
            label={job.status}
            color={getStatusStyle(job.status).color}
            size="small"
            sx={{ mb: 1 }}
          />
          
          {/* Show Update Status button for jobs that can be updated */}
          {allowedStatusOptions.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => {
                setJobForStatusUpdate(job);
                setStatusDialogOpen(true);
              }}
            >
              {allowedStatusOptions.length === 1 
                ? `Mark as ${allowedStatusOptions[0]}` 
                : 'Update Status'}
            </Button>
          )}
        </Box>
      );
    } else {
      // Add debug logging for status values
      // console.log(`Job ${job.id} status: ${job.status}, jobCategory: ${jobCategory}`);
      
      // --- Non-Driver View ---
      // Check if status change is allowed for this user/context
      const canChangeStatus = allowedStatusOptions.length > 0;

      if (canChangeStatus) {
        // Render the dropdown for users who can change the status
        let displayedStatus = job.status;
        // If the current status is not in the allowed options (e.g., after a permission change), default to a valid one
        if (!allowedStatusOptions.includes(job.status)) {
           // Try to find a sensible default, maybe the first allowed option or 'Waiting'/'Dispatched'
           displayedStatus = allowedStatusOptions.includes('Waiting') ? 'Waiting' :
                             allowedStatusOptions.includes('Dispatched') ? 'Dispatched' :
                             allowedStatusOptions[0] || job.status; // Fallback to first allowed or current
        }

        return (
          <FormControl fullWidth size="small"> {/* Added size="small" */}
            <InputLabel id={`status-select-label-${job.id}`}>Status</InputLabel>
            <Select
              key={`${jobCategory}-${job.id}-${job.status}`} // Include job.status in key to force re-render on status change
              labelId={`status-select-label-${job.id}`}
              label="Status"
              value={displayedStatus}
              onChange={(e) => onJobStatusChange(job.id, e.target.value)}
              sx={{
                // Optional: Adjust background based on status for subtle indication
                // backgroundColor: alpha(theme.palette[getStatusChipColor(displayedStatus)]?.main || theme.palette.grey[300], 0.1),
              }}
            >
              {/* Generate menu items based on allowed options */}
              {allowedStatusOptions.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        // Render a static Chip for users who cannot change the status
        // Render a static Chip using the new styles
        const style = getStatusStyle(job.status);
        return <Chip icon={style.icon} label={job.status} color={style.color} size="small" />;
      }
      // --- End Non-Driver View ---
    }
  };

  // Helper function to check if a user can reactivate completed jobs
  // This function is kept for future reference but marked as unused with eslint-disable
  // eslint-disable-next-line no-unused-vars
  const canReactivateJob = (user) => {
    if (!user) return false;
    
    // Use permission system to check if user can reactivate jobs
    return hasPermission('reactivateJobs');
  };
  
  // Helper function to check localStorage directly for DRIVER-ONLY users
  // This is used as a fallback during emergency render when user data isn't fully loaded
  // A driver-only user has primaryRole = 'N/A' and only 'driver' as secondary role
  const checkIfDriverFromLocalStorage = () => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) return false;
      
      // Parse the user data JSON
      const userData = JSON.parse(userDataString);
      
      // A user is "driver-only" if they have N/A primary role and ONLY driver secondary role
      let secondaryRoles = [];
      
      // Extract the secondary roles
      if (userData.secondaryRoles) {
        if (Array.isArray(userData.secondaryRoles)) {
          secondaryRoles = userData.secondaryRoles;
        } else if (typeof userData.secondaryRoles === 'object') {
          // Handle object format where values are booleans
          secondaryRoles = Object.keys(userData.secondaryRoles)
            .filter(role => userData.secondaryRoles[role] === true);
        }
      }
      
      // Check if this is a driver-only user (N/A primary role AND exactly one secondary role = 'driver')
      const isDriverOnly = 
        (userData.primaryRole === 'N/A' || userData.role === 'N/A') && 
        secondaryRoles.length === 1 && 
        secondaryRoles.includes('driver');
      
      // console.log('LocalStorage driver-only check:', { isDriverOnly, userData, secondaryRoles });
      return isDriverOnly;
    } catch (err) {
      console.error('Error checking localStorage for driver-only:', err);
      return false;
    }
  };

  // EMERGENCY RENDER: Use this state to force rendering after a timeout
  const [emergencyRender, setEmergencyRender] = useState(false);
  
  // Set up emergency render timeout - will force display after 5 seconds regardless of user data
  useEffect(() => {
    // console.log('Setting up emergency render timeout');
    const timer = setTimeout(() => {
      // console.log('EMERGENCY RENDER TIMEOUT - forcing display regardless of user data state');
      setEmergencyRender(true);
    }, 5000); // 5 second fail-safe
    return () => clearTimeout(timer);
  }, []);
  
  // Debug current user object
  useEffect(() => {
    // console.log('Current user object:', currentUser);
    if (currentUser) {
      // console.log('User properties:', {
      //   hasId: Boolean(currentUser.id),
      //   has_Id: Boolean(currentUser._id),
      //   hasUsername: Boolean(currentUser.username),
      //   keys: Object.keys(currentUser)
      // });
    }
  }, [currentUser]);
  
  // Check if user data is fully loaded - more aggressive checks including direct property access
  const isUserDataReady = Boolean(
    currentUser && 
    (currentUser.id || currentUser._id || 
     (currentUser.username && currentUser.primaryRole) ||
     forceRender || emergencyRender)
  );
  
  // Removed effectiveUser definition

  // Log when we're using the default user
  if (!currentUser && !userLoading) { // Only log if not loading and user is null
    // console.log('JobsTable: No current user data available.');
  }
 
  // --- GOA/Unsuccessful Request Submit Handlers ---
  const handleGoaRequestSubmit = async (reason) => {
    if (!jobForRequestDialog) return;
    const jobId = jobForRequestDialog.id;
    console.log(`Submitting GOA request for job ${jobId} with reason: ${reason}`);
    startLoading(`Requesting GOA for job ${jobId}...`);
    try {
      const token = currentUser?.token; // Get token from context
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/request-goa`, { reason }, config);
      setNotification({ open: true, message: 'GOA request submitted successfully.', severity: 'success' });
      refreshJobs(); // Refresh job list to show updated status
    } catch (error) {
      console.error('Error submitting GOA request:', error);
      setNotification({ open: true, message: `Failed to submit GOA request: ${error.response?.data?.message || error.message}`, severity: 'error' });
    } finally {
      // Stop loading is handled globally, no need here if using useLoading context properly
      setGoaRequestDialogOpen(false); // Close dialog in finally block
      setJobForRequestDialog(null);
    }
  };
 
  const handleUnsuccessfulRequestSubmit = async (reason) => {
    if (!jobForRequestDialog) return;
    const jobId = jobForRequestDialog.id;
    console.log(`Submitting Unsuccessful request for job ${jobId} with reason: ${reason}`);
    startLoading(`Requesting Unsuccessful for job ${jobId}...`);
    try {
      const token = currentUser?.token; // Get token from context
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/request-unsuccessful`, { reason }, config);
      setNotification({ open: true, message: 'Unsuccessful request submitted successfully.', severity: 'success' });
      refreshJobs(); // Refresh job list
    } catch (error) {
      console.error('Error submitting Unsuccessful request:', error);
      setNotification({ open: true, message: `Failed to submit Unsuccessful request: ${error.response?.data?.message || error.message}`, severity: 'error' });
    } finally {
      setUnsuccessfulRequestDialogOpen(false); // Close dialog in finally block
      setJobForRequestDialog(null);
    }
  };
  // --- End Submit Handlers ---
 
  // FINAL FIX: Force display table in most scenarios to prevent infinite loading
  // 1. If we have jobs and the emergency render timer has triggered
  // 2. If we have jobs and user data appears ready by any criteria
  // 3. After 5 seconds, the emergency render will force display regardless
  
  const shouldShowTable = 
    // Show table if we have jobs and either emergency render kicked in OR user data is ready
    (jobs.length > 0 && (emergencyRender || isUserDataReady)) ||
    // ALSO force display if emergency render is active, even if jobs array is empty
    (emergencyRender && !loading && !userLoading); // Consider userLoading state too

  // console.log('JobsTable rendering state:', {
  //   loading, // Job loading state
  //   userLoading, // User loading state
  //   emergencyRender,
  //   isUserDataReady,
  //   shouldShowTable,
  //   jobCount: jobs.length
  // });
  
  // Show loading state if either jobs or user data is loading AND emergency render hasn't kicked in
  if ((loading || userLoading) && !emergencyRender) {
    // Show Skeleton loader matching table structure
    const skeletonRowCount = 5; // Number of skeleton rows to display
    return (
      <Box sx={{ overflowX: 'auto', mt: 2 }}> {/* Added mt: 2 for spacing */}
        <Table>
          <TableHead>
            <TableRow>
              {/* Match header cells */}
              <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={80} /></TableCell>
              <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={100} /></TableCell>
              <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={120} /></TableCell>
              {/* Truck column skeleton (conditionally displayed) */}
              {!isXs && <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={80} /></TableCell>}
              {/* Account column skeleton (conditionally displayed) */}
              {!isXs && <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={100} /></TableCell>}
              <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={100} /></TableCell>
              <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={150} /></TableCell>
              <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={100} /></TableCell>
              <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={80} /></TableCell>
              <TableCell align="right" sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}><Skeleton variant="text" width={120} /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(skeletonRowCount)].map((_, index) => (
              <TableRow key={index}>
                <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
                {/* Truck column skeleton (conditionally displayed) */}
                {!isXs && <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>}
                {/* Account column skeleton (conditionally displayed) */}
                {!isXs && <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>}
                <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
                <TableCell align="right" sx={{ py: 1.5, px: 2 }}><Skeleton variant="text" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  // Show empty state when there are no jobs to display (and not loading)
  if (jobs.length === 0 && !loading && !userLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column" // Arrange icon and text vertically
        justifyContent="center"
        alignItems="center" // Center items horizontally
        my={6} // Increased vertical margin
        sx={{ color: 'text.secondary' }} // Apply secondary color to the container
      >
        <InfoOutlinedIcon sx={{ fontSize: 40, mb: 1 }} /> {/* Added Icon */}
        <Typography variant="h6" component="p"> {/* Increased font size */}
          No jobs found for this category.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Conditional Rendering: Table for larger screens, Cards for smaller screens */}
      {!isXs && (
        <> {/* Wrap conditional block in fragment */}
          {/* Wrap Table in TableContainer for sticky header and scrolling */}
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}> {/* Adjust maxHeight as needed */}
        <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 100 }}>Job ID</TableCell>
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 150 }}>Created</TableCell>
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 150 }}>Driver</TableCell>
            {/* Hide Truck on xs screens */}
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', display: { xs: 'none', sm: 'table-cell' }, minWidth: 100 }}>Truck</TableCell>
            {/* Hide Account on xs screens */}
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', display: { xs: 'none', sm: 'table-cell' }, minWidth: 120 }}>Account</TableCell>
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 120 }}>Service</TableCell>
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 180 }}>Location</TableCell>
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 150 }}>Status</TableCell>
            <TableCell sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 120 }}>ETA</TableCell>
            <TableCell align="right" sx={{ py: 1, px: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)', minWidth: 200 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <React.Fragment key={job.id}>
              {/* Removed background color based on status - rely on Chip */}
              <TableRow>
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* PO Number */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'medium', 
                        color: job.automatedDispatchEnabled ? 'green' : 'red' // Color based on toggle
                      }}
                    >
                      {job.po || job.id} 
                    </Typography> 
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}>{job.created}</TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  {renderDriverCell(job)}
                </TableCell>
                {/* Hide Truck on xs screens */}
                <TableCell sx={{ py: 1.5, px: 2, display: { xs: 'none', sm: 'table-cell' } }}>{job.truck || 'N/A'}</TableCell>
                {/* Hide Account on xs screens */}
                <TableCell sx={{ py: 1.5, px: 2, display: { xs: 'none', sm: 'table-cell' } }}>{job.account}</TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}>{job.service}</TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}>{jobLocationText(job)}</TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  {renderStatusCell(job)}
                </TableCell>
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  {/* Hide ETA for completed and canceled jobs */}
                  {jobCategory !== 'completed' && jobCategory !== 'canceled' ? (
                    job.eta ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {job.eta}
                        </Typography>
                        {/* Only show timer if NOT completed */}
                        {job.status !== 'Completed' && calculateTargetTime(job) && (
                          <CountdownTimer targetTime={calculateTargetTime(job)} />
                        )}
                      </Box>
                    ) : (
                      "N/A"
                    )
                  ) : (
                    "—" // Em dash for completed/canceled jobs
                  )}
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, px: 2 }} style={{ minWidth: '200px' }}>
                  <Box display="flex" justifyContent="flex-end">
{/* Automated Dispatch Toggle - Show for relevant pending statuses if user can dispatch AND is not an SP user */}
{['Pending', 'Waiting', 'Pending Acceptance', 'Pending Driver Acceptance', 'Searching SP'].includes(job.status) && canDispatchJobs() && currentUser?.primaryRole !== 'SP' && (
  <Tooltip title={`Automated Dispatch ${job.automatedDispatchEnabled ? 'ON' : 'OFF'}`}>
    <Switch
      checked={job.automatedDispatchEnabled}
      onChange={() => handleToggleAutomatedDispatch(job.id, startLoading, refreshJobs, setJobs, setNotification)} // Pass setNotification
      size="small"
      color={job.automatedDispatchEnabled ? 'success' : 'error'}
      sx={{ mr: 1 }} // Add some margin if needed
    />
  </Tooltip>
)}
{/* Blast Dispatch - broadcast the offer to all providers within a radius */}
{['Pending', 'Waiting', 'Pending Acceptance', 'Pending Driver Acceptance', 'Searching SP', 'Scheduled', 'Blast Dispatch'].includes(job.status) && canDispatchJobs() && currentUser?.primaryRole !== 'SP' && currentUser?.organizationLevel === 1 && onBlastJob && (
  <Tooltip title={job.status === 'Blast Dispatch' ? 'Blast in progress — click to re-blast with a new radius' : 'Blast to all providers within a radius'}>
    <IconButton aria-label="Blast dispatch" onClick={() => onBlastJob(job)}>
      <CampaignIcon color={job.status === 'Blast Dispatch' ? 'warning' : 'inherit'} />
    </IconButton>
  </Tooltip>
)}
{/* Card payment - completed credit-card jobs need temporary card details sent to the provider */}
{job.status === 'Completed' && job.paymentType === 'Credit Card' && !job.cardPaymentInfo?.sentAt && currentUser?.organizationLevel === 1 && canDispatchJobs() && onCardPayment && (
  <Tooltip title="Send temporary card payment details to the provider">
    <IconButton aria-label="Send card payment" onClick={() => onCardPayment(job)}>
      <CreditCardIcon color="error" />
    </IconButton>
  </Tooltip>
)}
{job.status === 'Completed' && job.paymentType === 'Credit Card' && job.cardPaymentInfo?.sentAt && (
  <Tooltip title={`Card payment sent ${new Date(job.cardPaymentInfo.sentAt).toLocaleString()} (ending ${job.cardPaymentInfo.last4})`}>
    <CreditCardIcon color="success" sx={{ alignSelf: 'center', mx: 1 }} />
  </Tooltip>
)}
                    <Tooltip title={job.expanded ? "Collapse details" : "Expand details"}>
                      <IconButton aria-label={job.expanded ? "Collapse job details" : "Expand job details"} onClick={() => onExpandClick(job.id)}>
                        {job.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit job">
                      <IconButton aria-label="Edit job" onClick={() => onEditJob && onEditJob(job)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More job actions">
                      <IconButton aria-label="More job actions" onClick={(e) => handleMenuClick(e, job)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    {/* Only show delete icon in canceled tab and if user has permissions */}
                    {jobCategory === 'canceled' && canDeleteJobs && (
                      <Tooltip title="Delete job">
                        <IconButton aria-label="Delete job" onClick={() => handleDeleteClick(job)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              {job.expanded && (
                <TableRow>
                  <TableCell colSpan={10} style={{ paddingBottom: 0, paddingTop: 0 }}>
                    <Collapse in={job.expanded} timeout="auto" unmountOnExit>
                      {/* Remove currentUser prop from JobDetails */}
                      <JobDetails job={job} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
        </Table>
          </TableContainer> {/* Closing TableContainer */}
        </> /* Close fragment */
      )}

      {isXs && (
        <Box sx={{ mt: 2 }}> {/* Container for cards */}
          {jobs.map((job) => (
            <Card key={job.id} sx={{ mb: 2 }} elevation={2}>
              {/* Restructure CardContent for better mobile layout */}
              <CardContent sx={{ pt: 1.5, pb: 0.5 }}> {/* Adjusted padding */}
                <Stack spacing={1}> {/* Use Stack for vertical spacing */}
                  {/* Row 1: ID and Status */}
                  <Grid container spacing={1} justifyContent="space-between" alignItems="flex-start">
                    <Grid item>
                      <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'medium' }}>
                        Job: {job.po || job.id}
                      </Typography>
                    </Grid>
                    <Grid item sx={{ textAlign: 'right' }}>
                      {/* Render Status - Use existing logic */}
                      {renderStatusCell(job)}
                    </Grid>
                  </Grid>

                  {/* Row 2: Location */}
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    Loc: {jobLocationText(job)}
                  </Typography>

                  {/* Row 3: Created */}
                  <Typography variant="body2" color="text.secondary">
                    Created: {job.created}
                  </Typography>

                  {/* Row 4: Driver Info/Actions */}
                  <Box sx={{ pt: 1 }}> {/* Add some top padding before driver info */}
                    {/* Render Driver Info/Actions - Use existing logic */}
                    {renderDriverCell(job)}
                  </Box>
                  {/* TODO: Add other relevant details (Account, Service, ETA?) if needed */}
                </Stack>
              </CardContent>
              {/* Add gap for spacing between actions */}
              <CardActions sx={{ justifyContent: 'flex-end', pt: 0.5, pb: 1, flexWrap: 'wrap', gap: 0.5 }}> {/* Adjusted padding & added gap */}
                 {/* Copy existing actions from TableCell */}
                 {['Pending', 'Waiting', 'Pending Acceptance', 'Pending Driver Acceptance', 'Searching SP'].includes(job.status) && canDispatchJobs() && currentUser?.primaryRole !== 'SP' && (
                   <Tooltip title={`Automated Dispatch ${job.automatedDispatchEnabled ? 'ON' : 'OFF'}`}>
                     <Switch
                       checked={job.automatedDispatchEnabled}
                       onChange={() => handleToggleAutomatedDispatch(job.id, startLoading, refreshJobs, setJobs, setNotification)}
                       size="small"
                       color={job.automatedDispatchEnabled ? 'success' : 'error'}
                     />
                   </Tooltip>
                 )}
                 {['Pending', 'Waiting', 'Pending Acceptance', 'Pending Driver Acceptance', 'Searching SP', 'Scheduled', 'Blast Dispatch'].includes(job.status) && canDispatchJobs() && currentUser?.primaryRole !== 'SP' && currentUser?.organizationLevel === 1 && onBlastJob && (
                   <Tooltip title={job.status === 'Blast Dispatch' ? 'Blast in progress — click to re-blast with a new radius' : 'Blast to all providers within a radius'}>
                     <IconButton aria-label="Blast dispatch" size="small" onClick={() => onBlastJob(job)}>
                       <CampaignIcon color={job.status === 'Blast Dispatch' ? 'warning' : 'inherit'} />
                     </IconButton>
                   </Tooltip>
                 )}
                 <Tooltip title={job.expanded ? "Collapse details" : "Expand details"}>
                   <IconButton aria-label={job.expanded ? "Collapse job details" : "Expand job details"} size="small" onClick={() => onExpandClick(job.id)}>
                     {job.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                   </IconButton>
                 </Tooltip>
                 <Tooltip title="Edit job">
                   <IconButton aria-label="Edit job" size="small" onClick={() => onEditJob && onEditJob(job)}>
                     <EditIcon />
                   </IconButton>
                 </Tooltip>
                 <Tooltip title="More job actions">
                   <IconButton aria-label="More job actions" size="small" onClick={(e) => handleMenuClick(e, job)}>
                     <MoreVertIcon />
                   </IconButton>
                 </Tooltip>
                 {jobCategory === 'canceled' && canDeleteJobs && (
                   <Tooltip title="Delete job">
                     <IconButton aria-label="Delete job" size="small" onClick={() => handleDeleteClick(job)}>
                       <DeleteIcon />
                     </IconButton>
                   </Tooltip>
                 )}
              </CardActions>
              {/* Collapsible Details Section */}
              <Collapse in={job.expanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ pt: 0 }}>
                  <JobDetails job={job} />
                </CardContent>
              </Collapse>
            </Card>
          ))}
        </Box>
      )}


      {/* Menus and Dialogs rendered outside the conditional layout */}
      {/* Menu for job actions */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {/* Reassign Option - Show if user can dispatch and job status is appropriate */}
        {selectedJobForMenu && canDispatchJobs() && !['Pending', 'Completed', 'Canceled', 'Awaiting Approval'].includes(selectedJobForMenu.status) && (
          <MenuItem onClick={() => {
            onDispatchJob(selectedJobForMenu); // Trigger dispatch dialog for reassignment
            handleMenuClose(); // Close menu after clicking
          }}>
            Reassign
          </MenuItem>
        )}
        {/* Duplicate: Org 1 dispatchers and above only (dispatcher/admin
            secondary roles or OW/sOW/RM primaries) */}
        {(() => {
          const rolesRaw = currentUser?.secondaryRoles || {};
          const rolesArr = Array.isArray(rolesRaw) ? rolesRaw : Object.keys(rolesRaw).filter(k => rolesRaw[k] === true);
          const canDuplicate = currentUser?.organizationLevel === 1 && (
            rolesArr.includes('admin') || rolesArr.includes('dispatcher') ||
            ['OW', 'sOW', 'OWp', 'RM'].includes(currentUser?.primaryRole)
          );
          return canDuplicate ? (
            <MenuItem onClick={() => handleMenuActionSelect('duplicate')}>Duplicate Job</MenuItem>
          ) : null;
        })()}
        {/* Mark GOA Option */}
        {selectedJobForMenu && selectedJobForMenu.status === 'On Site' && hasPermission('markJobsGoa') && (
          <MenuItem onClick={() => handleMenuActionSelect('markGOA')}>Mark GOA</MenuItem>
        )}
        {/* Mark Unsuccessful Option */}
        {selectedJobForMenu && selectedJobForMenu.status === 'On Site' && hasPermission('markJobsUnsuccessful') && (
          <MenuItem onClick={() => handleMenuActionSelect('markUnsuccessful')}>Mark Unsuccessful</MenuItem>
        )}
        {/* Unassign from Service Provider/Driver - For Org 1 users and Org 2 Admin/Dispatcher users */}
        {selectedJobForMenu && (
          (getUserOrganizationLevel(currentUser) === 1 && selectedJobForMenu.assignedVendorNumber) ||
          (getUserOrganizationLevel(currentUser) === 2 &&
           (hasPermission('admin') || hasPermission('dispatcher')) &&
           selectedJobForMenu.assignedVendorNumber &&
           selectedJobForMenu.vendorNumber === currentUser.vendorNumber)
        ) && (
          <MenuItem onClick={() => handleMenuActionSelect(
            getUserOrganizationLevel(currentUser) === 2 ? 'unassignDriver' : 'unassignFromSP'
          )}>
            <ListItemIcon>
              <PersonRemoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={
              selectedJobForMenu.driverId ? "Unassign Driver" : "Unassign from Service Provider"
            } />
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuActionSelect('cancel')}>Cancel Job</MenuItem>
        {/* Add more menu items as needed */}
      </Menu>

      {/* SP Assignment Cancellation Menu */}
      <Menu
        anchorEl={spMenuAnchorEl}
        open={Boolean(spMenuAnchorEl)}
        onClose={handleSPMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedJobForSPMenu && onCancelSPAssignment) {
            onCancelSPAssignment(selectedJobForSPMenu.id);
          }
          handleSPMenuClose();
        }}>
          <ListItemIcon>
            <SkipNextIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel SP & Find Next</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
           if (selectedJobForSPMenu && currentUser) {
             onMenuAction('cancel', selectedJobForSPMenu, currentUser); // Reuse existing cancel action
           }
          handleSPMenuClose();
        }}>
           <ListItemIcon>
            <CancelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel Job Entirely</ListItemText>
        </MenuItem>
      </Menu>

      {/* Org 1 Driver Assignment Menu */}
      <Menu
        anchorEl={driverMenuAnchorEl}
        open={Boolean(driverMenuAnchorEl)}
        onClose={handleDriverMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedJobForDriverMenu && onFindNextOrg1Driver) {
            onFindNextOrg1Driver(selectedJobForDriverMenu.id);
          }
          handleDriverMenuClose();
        }}>
          <ListItemIcon>
            <FindReplaceIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Find Another Driver</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
           if (selectedJobForDriverMenu && currentUser) {
             onMenuAction('cancel', selectedJobForDriverMenu, currentUser); // Reuse existing cancel action
           }
          handleDriverMenuClose();
        }}>
           <ListItemIcon>
            <CancelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel Job Entirely</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete job {jobToDelete?.po || jobToDelete?.id}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Status update dialog for drivers */}
      {jobForStatusUpdate && currentUser && ( // Ensure currentUser exists before rendering dialog
        <StatusUpdateDialog
          open={statusDialogOpen}
          onClose={() => setStatusDialogOpen(false)}
          currentStatus={jobForStatusUpdate.status}
          allowedStatuses={getAllowedStatusOptions(jobForStatusUpdate, jobCategory)} // Removed user argument
          onUpdateStatus={(newStatus) => {
            onJobStatusChange(jobForStatusUpdate.id, newStatus);
            setStatusDialogOpen(false);
          }}
        />
      )}

      {/* GOA Approval Confirmation Dialog */}
      <Dialog
        open={approveGOADialogOpen}
        onClose={() => setApproveGOADialogOpen(false)}
      >
        <DialogTitle>Approve GOA Request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve the GOA request for job {jobForGOAAction?.po || jobForGOAAction?.id}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveGOADialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            if (currentUser) { // Ensure currentUser exists
              onMenuAction('approveGOA', jobForGOAAction, currentUser); // Pass currentUser
            }
            setApproveGOADialogOpen(false);
          }} color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* GOA Denial Confirmation Dialog */}
      <Dialog
        open={denyGOADialogOpen}
        onClose={() => setDenyGOADialogOpen(false)}
      >
        <DialogTitle>Deny GOA Request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deny the GOA request for job {jobForGOAAction?.po || jobForGOAAction?.id}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyGOADialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
             if (currentUser) { // Ensure currentUser exists
              onMenuAction('denyGOA', jobForGOAAction, currentUser); // Pass currentUser
            }
            setDenyGOADialogOpen(false);
          }} color="error">
            Deny
          </Button>
        </DialogActions>
      </Dialog>
 
      {/* GOA Request Dialog */}
      <GoaRequestDialog
        open={goaRequestDialogOpen}
        onClose={() => {
          setGoaRequestDialogOpen(false);
          setJobForRequestDialog(null);
        }}
        onSubmit={handleGoaRequestSubmit}
      />
 
      {/* Unsuccessful Request Dialog */}
      <UnsuccessfulRequestDialog
        open={unsuccessfulRequestDialogOpen}
        onClose={() => {
          setUnsuccessfulRequestDialogOpen(false);
          setJobForRequestDialog(null);
        }}
        onSubmit={handleUnsuccessfulRequestSubmit}
      />

      {/* Unsuccessful Approval Confirmation Dialog */}
      <Dialog
        open={approveUnsuccessfulDialogOpen}
        onClose={() => setApproveUnsuccessfulDialogOpen(false)}
      >
        <DialogTitle>Approve Unsuccessful Request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve the Unsuccessful request for job {jobForUnsuccessfulAction?.po || jobForUnsuccessfulAction?.id}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveUnsuccessfulDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            if (currentUser) {
              onMenuAction('approveUnsuccessful', jobForUnsuccessfulAction, currentUser);
            }
            setApproveUnsuccessfulDialogOpen(false);
          }} color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>
 
      {/* Unsuccessful Denial Confirmation Dialog */}
      <Dialog
        open={denyUnsuccessfulDialogOpen}
        onClose={() => setDenyUnsuccessfulDialogOpen(false)}
      >
        <DialogTitle>Deny Unsuccessful Request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deny the Unsuccessful request for job {jobForUnsuccessfulAction?.po || jobForUnsuccessfulAction?.id}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyUnsuccessfulDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
             if (currentUser) {
              onMenuAction('denyUnsuccessful', jobForUnsuccessfulAction, currentUser);
            }
            setDenyUnsuccessfulDialogOpen(false);
          }} color="error">
            Deny
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JobsTable;